import {
  useCallback,
  useEffect,
  useState,
  type MouseEvent as ReactMouseEvent,
} from "react";
import { LayoutGroup } from "framer-motion";
import { useDND } from "../../contexts/DNDContext";
import { useServerNotifications } from "../../contexts/NotificationsContext";
import { FintheonBrandLockup } from "../brand/FintheonBrandLockup";
import {
  getSidebarOrder,
  setSidebarOrder,
  type NavTabId,
} from "../../lib/layoutOrderStorage";
import type { SurfaceCapabilities } from "../../lib/surface-capabilities";
import { FadingRuler } from "../shared/FadingRuler";
import { formatBadgeCount } from "./SidebarNavChrome";
import { SidebarNavItem } from "./SidebarNavItem";
import {
  getOrderedSidebarItems,
  sidebarGreeting,
  type NavTab,
} from "./sidebar-nav-model";

interface NavSidebarProps {
  activeTab: NavTab;
  onTabChange: (tab: NavTab) => void;
  onLogout: () => void;
  topStepXEnabled?: boolean;
  onOverlayVisibilityChange?: (visible: boolean) => void;
  onNotificationCenterToggle?: () => void;
  onRefinementClick?: () => void;
  refinementEnabled?: boolean;
  refinementActive?: boolean;
  capabilities?: SurfaceCapabilities;
}

const SIDEBAR_WIDTH_PX = 44;

export function NavSidebar({
  activeTab,
  onTabChange,
  onLogout,
  topStepXEnabled = false,
  onOverlayVisibilityChange,
  onNotificationCenterToggle,
  onRefinementClick,
  refinementEnabled = false,
  refinementActive = false,
  capabilities,
}: NavSidebarProps) {
  const { dndActive, toggleManualDnd, queueCount } = useDND();
  const { unreadCount: serverUnread } = useServerNotifications();
  const totalBadgeCount = queueCount + serverUnread;
  const [manualExpand, setManualExpand] = useState(false);
  const [order, setOrder] = useState<NavTabId[]>(() => getSidebarOrder());
  const [draggingId, setDraggingId] = useState<NavTabId | null>(null);
  const expanded = manualExpand;
  const isCollapsed = !expanded;
  const selectedTab = refinementActive ? "refinement" : activeTab;
  const instanceName =
    import.meta.env.VITE_FINTHEON_INSTANCE_NAME || "Fintheon";

  const handleRailDoubleClick = useCallback(
    (event: ReactMouseEvent<HTMLElement>) => {
      if (expanded) return;
      const target = event.target;
      if (!(target instanceof HTMLElement)) return;
      if (
        target.closest(
          'button,a,input,textarea,select,[role="button"],[data-sidebar-interactive="true"]',
        )
      ) {
        return;
      }
      setManualExpand(true);
    },
    [expanded],
  );

  const handleDragStart = useCallback((e: React.DragEvent, id: NavTabId) => {
    e.dataTransfer.setData("text/plain", id);
    e.dataTransfer.effectAllowed = "move";
    setDraggingId(id);
  }, []);

  const handleDragOver = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    e.dataTransfer.dropEffect = "move";
  }, []);

  const handleDrop = useCallback((e: React.DragEvent, targetId: NavTabId) => {
    e.preventDefault();
    setDraggingId(null);
    const sourceId = e.dataTransfer.getData("text/plain") as NavTabId | "";
    if (!sourceId || sourceId === targetId) return;
    setOrder((prev) => {
      const next = [...prev];
      const sourceIndex = next.indexOf(sourceId);
      const targetIndex = next.indexOf(targetId);
      if (sourceIndex === -1 || targetIndex === -1) return prev;
      next.splice(sourceIndex, 1);
      next.splice(targetIndex, 0, sourceId);
      setSidebarOrder(next);
      return next;
    });
  }, []);

  useEffect(() => {
    const onToggle = () => setManualExpand((value) => !value);
    window.addEventListener("fintheon:toggle-nav-sidebar", onToggle);
    return () =>
      window.removeEventListener("fintheon:toggle-nav-sidebar", onToggle);
  }, []);

  useEffect(() => {
    window.dispatchEvent(
      new CustomEvent("fintheon:nav-sidebar-state", {
        detail: { open: expanded },
      }),
    );
  }, [expanded]);

  useEffect(() => {
    onOverlayVisibilityChange?.(topStepXEnabled && expanded);
  }, [onOverlayVisibilityChange, topStepXEnabled, expanded]);

  useEffect(() => {
    document.documentElement.style.setProperty(
      "--fintheon-sidebar-width",
      `${topStepXEnabled ? 0 : SIDEBAR_WIDTH_PX}px`,
    );
    return () => {
      document.documentElement.style.setProperty(
        "--fintheon-sidebar-width",
        "0px",
      );
    };
  }, [topStepXEnabled]);

  if (topStepXEnabled) return null;

  const orderedItems = getOrderedSidebarItems({ order, capabilities });
  const badge = totalBadgeCount > 0 ? formatBadgeCount(totalBadgeCount) : null;

  return (
    <LayoutGroup id="fintheon-sidebar-nav">
      <aside
        className={`fintheon-power-frame-surface relative flex h-full flex-col border-r-0 py-3 transition-[width] duration-300 ease-in-out ${
          expanded ? "w-48" : "w-11"
        }`}
        onDoubleClick={handleRailDoubleClick}
        data-collapsed={isCollapsed}
        aria-label="Fintheon navigation"
      >
        {!isCollapsed && (
          <header className="px-3 pb-3 pt-0">
            <div className="sidebar-nav-header-row relative -top-3">
              <div className="min-w-0 leading-tight">
                <FintheonBrandLockup
                  title={instanceName}
                  className="sidebar-nav-brand-lockup"
                  iconClassName="sidebar-nav-brand-icon"
                  textClassName="sidebar-nav-brand"
                />
                <span className="sidebar-nav-greeting">
                  {sidebarGreeting()}
                </span>
              </div>
            </div>
            <FadingRuler className="opacity-35" />
          </header>
        )}

        <nav className="flex-1 space-y-1 px-1.5" aria-label="Primary">
          {orderedItems.map(({ tabId, icon, label, description }) => {
            return (
              <div
                key={tabId}
                draggable
                onDragStart={(e) => handleDragStart(e, tabId)}
                onDragOver={handleDragOver}
                onDrop={(e) => handleDrop(e, tabId)}
                onDragEnd={() => setDraggingId(null)}
                data-sidebar-row-dragging={
                  draggingId === tabId ? "true" : undefined
                }
                className="sidebar-nav-sortable-row relative flex items-center gap-1"
              >
                <SidebarNavItem
                  icon={icon}
                  label={label}
                  description={description}
                  isActive={selectedTab === tabId}
                  isCollapsed={isCollapsed}
                  onClick={() => onTabChange(tabId)}
                  dataTourTarget={tabId}
                />
              </div>
            );
          })}
        </nav>

        <nav className="space-y-1 px-1.5" aria-label="Utilities">
          {refinementEnabled && (
            <SidebarNavItem
              icon="cube-focus"
              label="Refinement"
              description="Scoring calibration"
              isActive={selectedTab === "refinement"}
              isCollapsed={isCollapsed}
              onClick={() => onRefinementClick?.()}
            />
          )}
          {!topStepXEnabled && (
            <SidebarNavItem
              icon={dndActive ? "bell-off" : "bell"}
              label={dndActive ? "Do Not Disturb" : "Notifications"}
              description={
                dndActive ? `${totalBadgeCount} queued` : "Click to enable DND"
              }
              isCollapsed={isCollapsed}
              isStateActive={dndActive}
              badge={badge}
              onClick={() => {
                if (totalBadgeCount > 0) onNotificationCenterToggle?.();
                else toggleManualDnd();
              }}
              tooltip={dndActive ? "Do Not Disturb (ON)" : "Notifications"}
              dataTourTarget="notifications"
            />
          )}
          {capabilities?.allowPerformance !== false && (
            <SidebarNavItem
              icon="book-open-check"
              label="Performance"
              description="ER history & KPIs"
              isActive={selectedTab === "performance"}
              isCollapsed={isCollapsed}
              onClick={() => onTabChange("performance")}
              dataTourTarget="performance"
            />
          )}
          <SidebarNavItem
            icon="settings"
            label="Settings"
            description="Preferences & configuration"
            isActive={selectedTab === "settings"}
            isCollapsed={isCollapsed}
            onClick={() => onTabChange("settings")}
            dataTourTarget="settings"
          />
          <SidebarNavItem
            icon="logout"
            label="Logout"
            isCollapsed={isCollapsed}
            onClick={onLogout}
            isDanger
          />
        </nav>
      </aside>
    </LayoutGroup>
  );
}
